import type { Column } from '../../claim-history/simple-table'

import { zodResolver } from '@hookform/resolvers/zod'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { MdAdd, MdCheck } from 'react-icons/md'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import { animated, useSpring } from 'react-spring'
// @ts-expect-error // It's fine
import PartnerOrganizations from 'shared_src/dictionaries/partnerOrganizations'
import { useDebounceValue } from 'usehooks-ts'

import { SystemRoles } from '../../../lib/Codes'
import groups from '../../../lib/Codes/groups'
import { DiscardDialogContext } from '../../../lib/global-discard-dialog'
import http from '../../../lib/http'
import { notify } from '../../../lib/Notification/actions'
import { useAppDispatch, useAppSelector } from '../../../store'
import * as Table from '../../claim-history/simple-table'
import * as Grid from '../../shared/grid'
import { Checkbox } from '../../shared/NarusMaterial'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'
import { nonNullablePredicate } from '../mobile-management/mobile-users'

import { useClientTags } from './queries'
import { getSchema, round, Permission, formatPhoneNumber, type Values } from './schemas'

let roles: Role[] = SystemRoles
let organizations: Organization[] = PartnerOrganizations

interface User extends Record<string, unknown> {
  id: string

  first_name?: string | null
  last_name?: string | null
  suffix?: string | null
  contact?: string | null
  email?: string | null
  phone?: string | null

  onduty?: boolean | null
  monitoring?: boolean | null
  oncall?: boolean | null
  concierge?: boolean | null
  practitioner_notifications?: boolean | null
  enabled?: boolean | null

  organization?: string | null
  organizations?: string[] | null

  supervisor?: string | null
  director?: string | null
  role?: string | null

  trend?: boolean | null
  behavioral?: Permission | null
  mobile_management?: Permission | null

  target_load?: number | null
  expected_billable_hours?: number | null

  group?: string | null
  prn?: boolean | null
}

interface Role {
  code: string
  value: string
  label: string
}

interface Organization {
  value: string
  label: string
}

let permissionMapper: Record<Permission, string> = {
  [Permission.Read]: 'Read',
  [Permission.Write]: 'Write',
  [Permission.None]: 'None'
}

const enum Status {
  OnDuty = 'on-duty',
  Monitoring = 'monitoring',
  OnCall = 'on-call',
  Concierge = 'concierge',
  PractitionerNotifications = 'practitioner-notifications'
}

let statusMapper: Record<Status, string> = {
  [Status.OnDuty]: 'On Duty',
  [Status.Monitoring]: 'Monitoring',
  [Status.OnCall]: 'On Call',
  [Status.Concierge]: 'Concierge',
  [Status.PractitionerNotifications]: 'Practitioner Messages'
}

type QueryKey = [
  'users',
  SearchParams
]

interface QueryData {
  data: User[]
  totalFiltered: number
  totalUnfiltered: number
}

interface State {
  searchParams: SearchParams

  newUserForm: {
    isVisible: boolean
  }
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }
  | { type: 'new-user-form/visibility/set', payload: boolean }

interface SearchParams {
  query: string

  filters: {
    enabled: boolean[]
    status: Status[]
    behavioral: Permission[]
    mobile_management: Permission[]
    role: string[]
    organization: string[]
  }

  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'name',
  'role',
  'email',
  'phone',
  'organization',
  'enabled',
  'behavioral',
  'mobile_management'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      enabled: [
        true
      ],
      status: [],
      behavioral: [],
      mobile_management: [],
      role: [],
      organization: []
    },
    sort: {
      field: 'name',
      direction: 'asc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  },

  newUserForm: {
    isVisible: false
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    case 'new-user-form/visibility/set': {
      return {
        ...state,
        newUserForm: {
          ...state.newUserForm,
          isVisible: action.payload
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  name: 'first_name'
}

let filterMapper: Record<string, string> = {
  client_name: 'client_id'
}

function getUserLabel (user: Pick<User, 'first_name' | 'last_name' | 'suffix' | 'role'>): string {
  let label = [ user.first_name, user.last_name ]
    .map(v => v?.trim())
    .filter(nonNullablePredicate)
    .join(' ')

  if (typeof user.suffix === 'string' && user.suffix.trim().length > 0) {
    label = `${label}, ${user.suffix.trim()}`
  }

  if (typeof user.role === 'string' && user.role.trim().length > 0) {
    let role = roles.find(role => role.code === user.role)

    if (role) {
      label = `${label} (${role.label})`
    }
  }

  return label
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.enabled.length === initialState.searchParams.filters.enabled.length &&
    searchParams.filters.enabled.every(enabled => initialState.searchParams.filters.enabled.includes(enabled)) &&
    searchParams.filters.status.length === initialState.searchParams.filters.status.length &&
    searchParams.filters.status.every(status => initialState.searchParams.filters.status.includes(status)) &&
    searchParams.filters.behavioral.length === initialState.searchParams.filters.behavioral.length &&
    searchParams.filters.behavioral.every(behavioral => initialState.searchParams.filters.behavioral.includes(behavioral)) &&
    searchParams.filters.mobile_management.length === initialState.searchParams.filters.mobile_management.length &&
    searchParams.filters.mobile_management.every(mobileManagement => initialState.searchParams.filters.mobile_management.includes(mobileManagement)) &&
    searchParams.filters.role.length === initialState.searchParams.filters.role.length &&
    searchParams.filters.role.every(role => initialState.searchParams.filters.role.includes(role))
  )
}

export function UsersPage (): JSX.Element {
  let appDispatch = useAppDispatch()

  let [ state, dispatch ] = useReducer(reducer, initialState)
  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'users',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...filters.enabled.map(v => [ 'filters.enabled', v ? 'true' : 'false' ]),
        ...filters.status.map(v => [ 'filters.status', v ]),
        ...filters.behavioral.map(v => [ 'filters.behavioral', v ]),
        ...filters.mobile_management.map(v => [ 'filters.mobile_management', v ]),
        ...filters.role.map(v => [ 'filters.role', v ]),
        ...filters.organization.map(v => [ 'filters.organization', v ]),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      let response = await http.getRequest(`/admin/users?${sp.toString()}`)
      return response
    },

    placeholderData: keepPreviousData
  })

  let mutation = useMutation({
    async mutationFn (values: Values) {
      let id = 'id' in values ? values.id : undefined

      if (typeof id !== 'string') {
        await http.postRequest('/admin/users', values)
        return
      }

      await http.putRequest(`/admin/users?id=${id}`, values)
    },

    onSuccess () {
      appDispatch(notify('User saved successfully', false))
    },

    onError (err) {
      appDispatch(notify('Failed to save user', true))
      void err
    },

    async onSettled () {
      await query.refetch()
    }
  })

  let columns = useMemo<Array<Column<User>>>(() => [
    {
      name: 'name',
      header: 'Name',
      isSortable: true,
      style: {
        minWidth: '10rem'
      },
      getValue (entry) {
        let name = [ entry.first_name, entry.last_name ]
          .map(v => v?.trim())
          .filter(nonNullablePredicate)
          .join(' ')

        if (typeof entry.suffix === 'string' && entry.suffix.trim().length > 0) {
          name = `${name}, ${entry.suffix.trim()}`
        }

        return name || null
      }
    },
    {
      name: 'role',
      header: 'Role',
      isSortable: true,
      getValue (entry) {
        return roles?.find(role => role.code === entry.role)?.label ?? entry.role ?? null
      }
    },
    {
      name: 'email',
      header: 'Email',
      isSortable: true,
      gridArea: '12rem',
      style: {
        // hyphens: 'auto',
        wordBreak: 'break-all'
      }
    },
    // {
    //   name: 'phone',
    //   header: 'Alert Phone',
    //   isSortable: true
    // },
    {
      name: 'organization',
      header: 'Organization',
      isSortable: true,
      getValue (entry) {
        return organizations?.find(organization => organization.value === entry.organization)?.label ?? entry.organization ?? null
      }
    },
    {
      name: 'enabled',
      header: 'Enabled',
      isSortable: true,
      getValue (entry) {
        if (entry.enabled !== true) {
          return null
        }

        return (
          <MdCheck
            size={18}
            color='#3fae2a'
          />
        )
      }
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: false,
      getValue (entry) {
        let statuses = [
          entry.onduty && 'On Duty',
          entry.monitoring && 'Monitoring',
          entry.oncall && 'On Call',
          entry.concierge && 'Concierge',
          entry.practitioner_notifications && 'Practitioner Messages'
        ]

        return statuses
          .filter(Boolean)
          .join(', ') || null
      }
    },
    {
      name: 'behavioral',
      header: 'Behavioral',
      isSortable: true,
      getValue (entry) {
        if (!entry.behavioral || entry.behavioral === Permission.None) {
          return null
        }

        return permissionMapper[entry.behavioral]
      }
    },
    {
      name: 'mobile_management',
      header: 'Mobile Management',
      isSortable: true,
      getValue (entry) {
        if (!entry.mobile_management || entry.mobile_management === Permission.None) {
          return null
        }

        return permissionMapper[entry.mobile_management]
      }
    }
  ], [])

  let filters = useMemo(() => {
    return [
      {
        name: 'enabled',
        value: state.searchParams.filters.enabled,
        options: [
          { value: true, label: 'Enabled' },
          { value: false, label: 'Disabled' }
        ]
      },
      {
        name: 'status',
        value: state.searchParams.filters.status,
        options: [
          { value: Status.OnDuty, label: statusMapper[Status.OnDuty] },
          { value: Status.Monitoring, label: statusMapper[Status.Monitoring] },
          { value: Status.OnCall, label: statusMapper[Status.OnCall] },
          { value: Status.Concierge, label: statusMapper[Status.Concierge] },
          { value: Status.PractitionerNotifications, label: statusMapper[Status.PractitionerNotifications] }
        ]
      },
      {
        name: 'behavioral',
        value: state.searchParams.filters.behavioral,
        options: [
          { value: Permission.Read, label: permissionMapper[Permission.Read] },
          { value: Permission.Write, label: permissionMapper[Permission.Write] },
          { value: Permission.None, label: permissionMapper[Permission.None] }
        ]
      },
      {
        name: 'mobile_management',
        value: state.searchParams.filters.mobile_management,
        options: [
          { value: Permission.Read, label: permissionMapper[Permission.Read] },
          { value: Permission.Write, label: permissionMapper[Permission.Write] },
          { value: Permission.None, label: permissionMapper[Permission.None] }
        ]
      },
      {
        name: 'role',
        value: state.searchParams.filters.role,
        options: [
          ...roles
            .slice()
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(role => ({
              value: role.code,
              label: role.label
            })),
          {
            value: 'none',
            label: 'None'
          }
        ]
      },
      {
        name: 'organization',
        value: state.searchParams.filters.organization,
        options: [
          ...organizations
            .slice()
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(role => ({
              value: role.value,
              label: role.label
            })),
          {
            value: 'none',
            label: 'None'
          }
        ]
      }
    ]
  }, [
    state.searchParams.filters.enabled,
    state.searchParams.filters.status,
    state.searchParams.filters.behavioral,
    state.searchParams.filters.mobile_management,
    state.searchParams.filters.role,
    state.searchParams.filters.organization
  ])

  let handleFilterChange = useCallback(<T extends keyof User>(name: T, value: Array<User[T]>): void => {
    let mappedName = filterMapper[name as string] ?? name

    dispatch({
      type: 'filters/merge',
      payload: {
        [mappedName]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof User, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.totalFiltered ?? 0,
      totalUnfiltered: query.data?.totalUnfiltered ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  let addUserButtonStyles = useSpring({
    to: state.newUserForm.isVisible
      ? { transform: 'rotate(135deg)' }
      : { transform: 'rotate(0deg)' }
  })

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Users</span>
        </h1>

        <animated.button
          type='button'
          style={{
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#7729ff',
            color: '#fff',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            marginLeft: '-1rem',
            transformOrigin: 'center center',
            ...addUserButtonStyles
          }}
          onClick={() => {
            dispatch({
              type: 'new-user-form/visibility/set',
              payload: !state.newUserForm.isVisible
            })
          }}
        >
          <MdAdd
            size={18}
            color='#fff'
          />
        </animated.button>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by name, email, phone, enrollment type, client name and status'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
          onFiltersClear={isDefaultSearchParams(state.searchParams) ? undefined : handleFiltersClear}
        />
      </PageHeader>

      {state.newUserForm.isVisible && (
        <div
          style={{
            padding: '2.5rem',
            border: '1px solid #f3f3f3',
            boxShadow: '0 2.5182034969px 2.2138051987px 0 #00000005, 0 10px 30px 0 #0000000a, 0 -10px 30px 0 #0000000a',
            backgroundColor: '#fff',
            borderRadius: '1rem',
            marginBottom: '2rem'
          }}
        >
          <Form
            isSaving={mutation.isPending}
            onSubmit={values => {
              mutation.mutate(values, {
                onSuccess () {
                  dispatch({
                    type: 'new-user-form/visibility/set',
                    payload: false
                  })
                }
              })
            }}
            onCancel={() => {
              dispatch({
                type: 'new-user-form/visibility/set',
                payload: false
              })
            }}
          />
        </div>
      )}

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No users found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(item, _, setState) => {
          return (
            <ExpandingComponent
              user={item}
              isSaving={mutation.isPending}
              onSave={values => {
                // @ts-expect-error // It's fine
                mutation.mutate(values, {
                  onSuccess () {
                    setState(null)
                  }
                })
              }}
              onCancel={() => {
                setState(null)
              }}
            />
          )
        }}
      />
    </div>
  )
}

interface FormProps {
  initialValues?: Partial<Values>
  isSaving: boolean
  onSubmit: (values: Values) => void
  onCancel: () => void
}

function Form ({ initialValues, isSaving, onSubmit, onCancel }: FormProps): JSX.Element {
  let discardDialog = useContext(DiscardDialogContext)
  let { onFormChange, resetForm } = discardDialog ?? {}

  let form = useForm<Values>({
    defaultValues: {
      ...initialValues,
      first_name: initialValues?.first_name ?? '',
      last_name: initialValues?.last_name ?? '',
      suffix: initialValues?.suffix ?? null,
      email: initialValues?.email ?? '',

      contact: typeof initialValues?.contact === 'string'
        ? formatPhoneNumber(initialValues?.contact)
        : initialValues?.contact ?? null,

      phone: typeof initialValues?.phone === 'string'
        ? formatPhoneNumber(initialValues?.phone)
        : initialValues?.phone ?? '',

      behavioral: initialValues?.behavioral ?? Permission.None,
      mobile_management: initialValues?.mobile_management ?? Permission.None,
      uba_care: initialValues?.uba_care ?? Permission.None,
      onduty: initialValues?.onduty ?? false,
      monitoring: initialValues?.monitoring ?? false,
      oncall: initialValues?.oncall ?? false,
      concierge: initialValues?.concierge ?? false,
      practitioner_notifications: initialValues?.practitioner_notifications ?? false,
      trend: initialValues?.trend ?? true,
      prn: initialValues?.prn ?? false,
      // @ts-expect-error // It's fine
      target_load: initialValues?.target_load ?? '0',
      // @ts-expect-error // It's fine
      expected_billable_hours: initialValues?.expected_billable_hours ?? '0',
      // @ts-expect-error // It's fine
      organization: initialValues?.organization ?? null,
      supervisor: initialValues?.supervisor ?? null,
      director: initialValues?.director ?? null,
      // @ts-expect-error // It's fine
      role: initialValues?.role ?? null,
      group: initialValues?.group ?? null,
      organizations: initialValues?.organizations ?? [],
      client_tags: initialValues?.client_tags ?? []
    },
    resolver: async (values, context, options) => {
      return await zodResolver(getSchema(values.role))(values, context, options)
    }
  })

  let id = form.watch('id')
  let isNew = typeof id === 'undefined'
  let formKey = `admin/users/${id ?? 'new'}`

  let organizationsMap = useAppSelector(state => state.globalVars.organizations ?? {})
  let organizations2 = Object.entries(organizationsMap).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([ id, organization ]) => ({ id, ...organization }))

  let expectedBillableHours = form.watch('expected_billable_hours')
  let parsedExpectedBillableHours = typeof expectedBillableHours === 'string' ? parseFloat(expectedBillableHours) : undefined
  let roundedExpectedBillableHours = typeof parsedExpectedBillableHours === 'number' && !isNaN(parsedExpectedBillableHours) ? round(parsedExpectedBillableHours, 2) : undefined

  let submit = useCallback(async (values: Values) => {
    onSubmit(values)
  }, [ onSubmit ])

  let supervisorId = form.watch('supervisor')
  let [ supervisor, setSupervisor ] = useState<User>()
  let [ supervisorIsLoading, setSupervisorIsLoading ] = useState(false)

  let directorId = form.watch('director')
  let [ director, setDirector ] = useState<User>()
  let [ directorIsLoading, setDirectorIsLoading ] = useState(false)

  let role = form.watch('role')

  let loadOptions = useCallback(async (value: string) => {
    let response = await http.getRequest<QueryData>(`/admin/users?search=${value}&count=9999&on=first_name&dir=asc&filters.enabled=true`)

    return response.data
      .filter(user => user.id !== id)
      .map(user => ({
        value: user.id,
        label: getUserLabel(user)
      }))
  }, [ id ])

  const { data: clientTags } = useClientTags()

  useEffect(() => {
    if (typeof supervisorId !== 'string') {
      setSupervisor(undefined)
      return
    }

    setSupervisorIsLoading(true)

    http.getRequest<QueryData>(`/admin/users?search=${supervisorId}&count=1&on=first_name&dir=asc`)
      .then((response: QueryData) => {
        setSupervisor(response?.data?.[0])
      })
      .catch((err: unknown) => {
        console.error(err)
      })
      .finally(() => {
        setSupervisorIsLoading(false)
      })
  }, [ supervisorId ])

  useEffect(() => {
    if (typeof directorId !== 'string') {
      setSupervisor(undefined)
      return
    }

    setDirectorIsLoading(true)

    http.getRequest(`/admin/users?search=${directorId}&count=1&on=first_name&dir=asc`)
      .then((response: QueryData) => {
        setDirector(response?.data?.[0])
      })
      .catch((err: unknown) => {
        console.error(err)
      })
      .finally(() => {
        setDirectorIsLoading(false)
      })
  }, [ directorId ])

  useEffect(() => {
    if (form.formState.isDirty) {
      onFormChange?.(formKey)
    }

    if (!form.formState.isDirty) {
      resetForm?.(formKey)
    }
  }, [
    formKey,
    form.formState.isDirty,
    onFormChange,
    resetForm
  ])

  useEffect(() => {
    return () => {
      resetForm?.(formKey)
    }
  }, [
    formKey,
    resetForm
  ])

  return (
    <form
      noValidate
      autoComplete='off'
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={form.handleSubmit(submit)}
      style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}
    >
      <h3 style={{ fontSize: '1.25rem', fontWeight: '500', padding: 0, margin: 0, paddingBottom: '1rem' }}>
        Personal Info
      </h3>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='first_name'
          render={({ field, fieldState }) => (
            <Grid.Input
              required
              placeholder='First Name'
              {...field}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name='last_name'
          render={({ field, fieldState }) => (
            <Grid.Input
              required
              placeholder='Last Name'
              {...field}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name='suffix'
          render={({ field, fieldState }) => (
            <Grid.Input
              placeholder='Suffix'
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='contact'
          render={({ field, fieldState }) => (
            <Grid.Input
              placeholder='Contact Phone'
              helperText='mPower will display this number for this user. If a phone number is not provided, no number is presented.'
              {...field}
              mask='111.111.1111'
              value={typeof field.value === 'string' ? field.value : ''}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name='phone'
          render={({ field, fieldState }) => (
            <Grid.Input
              required
              placeholder='Alert Phone'
              helperText='Compassion will send SMS alerts to this phone number while the user is on-duty.'
              {...field}
              mask='111.111.1111'
              value={typeof field.value === 'string' ? field.value : ''}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>

      <hr />

      <h3 style={{ fontSize: '1.25rem', fontWeight: '500', padding: 0, margin: 0, paddingBottom: '1rem' }}>
        Notifications
      </h3>

      <div
        style={{
          display: 'grid',
          gap: '1.5rem',
          gridTemplateColumns: '1fr 1fr 1fr',
          gridAutoFlow: 'row',
          marginTop: '-0.825rem'
        }}
      >
        <Controller
          control={form.control}
          name='onduty'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
              <Checkbox
                label='On Duty'
                {...field}
              />
            </div>
          )}
        />

        <Controller
          control={form.control}
          name='monitoring'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
              <Checkbox
                label='Monitoring'
                {...field}
              />
            </div>
          )}
        />

        <Controller
          control={form.control}
          name='oncall'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
              <Checkbox
                label='On Call'
                {...field}
              />
            </div>
          )}
        />

        <Controller
          control={form.control}
          name='concierge'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
              <Checkbox
                label='Concierge'
                {...field}
              />
            </div>
          )}
        />

        <Controller
          control={form.control}
          name='practitioner_notifications'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
              <Checkbox
                label='Practitioner Messages'
                {...field}
              />
            </div>
          )}
        />
      </div>

      <hr />

      <h3 style={{ fontSize: '1.25rem', fontWeight: '500', padding: 0, margin: 0, paddingBottom: '1rem' }}>
        Preferences
      </h3>

      <div
        style={{
          display: 'grid',
          gap: '1.5rem',
          gridTemplateColumns: '1fr 1fr 1fr',
          gridAutoFlow: 'row',
          marginTop: '-0.825rem'
        }}
      >
        <Controller
          control={form.control}
          name='trend'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
              <Checkbox
                label='Trend'
                {...field}
              />
            </div>
          )}
        />
      </div>

      <hr />

      <h3 style={{ fontSize: '1.25rem', fontWeight: '500', padding: 0, margin: 0, paddingBottom: '1rem' }}>
        Permissions
      </h3>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='behavioral'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Behavioral Health'
              required
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                options={[ Permission.Read, Permission.Write, Permission.None ].map(value => ({
                  value,
                  label: permissionMapper[value]
                }))}
                value={field.value ? { value: field.value, label: permissionMapper[field.value] } : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />

        <Controller
          control={form.control}
          name='mobile_management'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Mobile Management'
              required
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                options={[ Permission.Read, Permission.Write, Permission.None ].map(value => ({
                  value,
                  label: permissionMapper[value]
                }))}
                value={field.value ? { value: field.value, label: permissionMapper[field.value] } : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />

        <Controller
          control={form.control}
          name='uba_care'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='UBACARE Orders'
              required
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                options={[ Permission.Write, Permission.None ].map(value => ({
                  value,
                  label: permissionMapper[value]
                }))}
                value={field.value ? { value: field.value, label: permissionMapper[field.value] } : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />
      </div>

      <hr />

      <h3
        style={{
          fontSize: '1.25rem',
          fontWeight: '500',
          padding: 0,
          margin: 0,
          paddingBottom: '1rem',
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'center'
        }}
      >
        <span>Account Settings</span>

        {!isNew && (
          <Controller
            control={form.control}
            name='enabled'
            render={({ field }) => (
              <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D' }}>
                <Checkbox
                  label='Account Enabled'
                  {...field}
                />
              </div>
            )}
          />
        )}
      </h3>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='email'
          render={({ field, fieldState }) => (
            <Grid.Input
              required
              type='email'
              placeholder='Email'
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
              errorMessage={fieldState.error?.message}
              disabled={!isNew}
              onChange={value => {
                if (!isNew) {
                  return
                }

                field.onChange(value)
              }}
              style={{
                cursor: !isNew
                  ? 'default'
                  : undefined
              }}
            />
          )}
        />

        {!isNew && (
          <Controller
            control={form.control}
            name='id'
            render={({ field, fieldState }) => (
              <Grid.Input
                required
                type='text'
                placeholder='ID'
                {...field}
                value={typeof field.value === 'string' ? field.value : ''}
                errorMessage={fieldState.error?.message}
                disabled
                onChange={() => {
                  // Do nothing
                }}
                style={{
                  fontFamily: 'monospace',
                  cursor: 'default'
                }}
              />
            )}
          />
        )}
      </div>

      {isNew && (
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <Controller
            control={form.control}
            name='password'
            render={({ field, fieldState }) => (
              <Grid.Input
                required
                placeholder='Password'
                type='password'
                {...field}
                value={typeof field.value === 'string' ? field.value : ''}
                errorMessage={fieldState.error?.message}
                autoComplete='new-password'
                helperText='Password must be at least 8 characters long and contain at least one lowercase letter, one uppercase letter and one digit'
              />
            )}
          />

          <Controller
            control={form.control}
            name='password_confirmation'
            render={({ field, fieldState }) => (
              <Grid.Input
                required
                placeholder='Password Confirmation'
                type='password'
                {...field}
                value={typeof field.value === 'string' ? field.value : ''}
                errorMessage={fieldState.error?.message}
                autoComplete='new-password'
              />
            )}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
        <Controller
          control={form.control}
          name='organization'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Managing Organization'
              required
              style={{ flexGrow: 1, flexBasis: '50%' }}
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                options={organizations.slice().sort((a, b) => a.label.localeCompare(b.label))}
                value={field.value
                  ? {
                    value: field.value,
                    label: organizations.find(organization => organization.value === field.value)?.label
                  }
                  : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />

        <Controller
          control={form.control}
          name='prn'
          render={({ field }) => (
            <div style={{ fontSize: '1rem', fontWeight: '400', color: '#3D3D3D', flexGrow: '1', flexBasis: '50%' }}>
              <Checkbox
                label='PRN'
                {...field}
              />
            </div>
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='supervisor'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Supervisor'
              errorMessage={fieldState.error?.message}
            >
              <AsyncSelect
                {...field}
                isClearable
                defaultOptions
                cacheOptions={false}
                isLoading={supervisorIsLoading}
                loadOptions={loadOptions}
                value={field.value
                  ? {
                    value: field.value,
                    label: supervisor ? getUserLabel(supervisor) : field.value
                  }
                  : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />

        <Controller
          control={form.control}
          name='director'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Medical Director'
              errorMessage={fieldState.error?.message}
            >
              <AsyncSelect
                {...field}
                isClearable
                defaultOptions
                cacheOptions={false}
                isLoading={directorIsLoading}
                loadOptions={loadOptions}
                value={field.value
                  ? {
                    value: field.value,
                    label: director ? getUserLabel(director) : field.value
                  }
                  : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='role'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Role'
              required
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                options={roles.map(role => ({
                  value: role.code,
                  label: role.label
                })).sort((a, b) => a.label.localeCompare(b.label))}
                value={field.value
                  ? {
                    value: field.value,
                    label: roles.find(role => role.code === field.value)?.label
                  }
                  : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />

        <Controller
          control={form.control}
          name='group'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Group'
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                isClearable
                options={groups.slice().sort((a, b) => a.label.localeCompare(b.label))}
                value={field.value
                  ? {
                    value: field.value,
                    label: groups.find(group => group.value === field.value)?.label
                  }
                  : null}
                onChange={(value) => {
                  field.onChange(value?.value ?? null)
                }}
                maxMenuHeight={200}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='target_load'
          render={({ field, fieldState }) => (
            <Grid.Input
              required
              placeholder='Target Load'
              {...field}
              value={typeof field.value === 'number' ? field.value.toString(10) : field.value ?? ''}
              errorMessage={fieldState.error?.message}
              helperText='Please, provide integer from 0 to 99'
            />
          )}
        />

        <Controller
          control={form.control}
          name='expected_billable_hours'
          render={({ field, fieldState }) => (
            <Grid.Input
              required
              placeholder='Expected Billable Hours'
              {...field}
              value={typeof field.value === 'number' ? field.value.toString(10) : field.value ?? ''}
              errorMessage={fieldState.error?.message}
              helperText={typeof parsedExpectedBillableHours === 'number' && typeof roundedExpectedBillableHours === 'number' && !isNaN(parsedExpectedBillableHours) && roundedExpectedBillableHours !== parsedExpectedBillableHours ? `Will be rounded to ${roundedExpectedBillableHours}` : 'Please, provide a number from 0 to 999 (2 decimal places allowed)'}
            />
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='organizations'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Organizations the user has access to'
              required
              errorMessage={fieldState.error?.message}
            >
              <Select
                {...field}
                isMulti
                closeMenuOnSelect={false}
                options={organizations2.map(organization => ({
                  value: organization.id,
                  label: organization.name
                })).sort((a, b) => a.label.localeCompare(b.label))}
                value={field.value
                  ? field.value.map(value => ({
                    value,
                    label: organizations2.find(organization => organization.id === value)?.name
                  })).sort((a, b) => a.label?.localeCompare(b?.label ?? '') ?? 0)
                  : null}
                onChange={(value) => {
                  field.onChange(value?.map(v => v.value) ?? null)
                }}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <Controller
          control={form.control}
          name='client_tags'
          render={({ field, fieldState }) => (
            <Grid.SelectWrapper
              label='Compassion View'
              errorMessage={fieldState.error?.message}
              required={role === 'broker_access'}
            >
              <Select
                {...field}
                isMulti
                closeMenuOnSelect={false}
                required={role === 'broker_access'}
                options={clientTags?.map(tag => ({
                  value: tag,
                  label: tag
                })).sort((a, b) => a.label.localeCompare(b.label))}
                value={field.value
                  ? field.value.map(value => ({
                    value,
                    label: value
                  })).sort((a, b) => a.label?.localeCompare(b?.label ?? '') ?? 0)
                  : null}
                onChange={(value) => {
                  field.onChange(value?.map(v => v.value) ?? null)
                }}
                styles={{
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#fff',
                    zIndex: 2
                  })
                }}
              />
            </Grid.SelectWrapper>
          )}
        />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-start' }}>
        <Grid.Button
          light
          type='button'
          style={{
            padding: '.8rem 3rem'
          }}
          onClick={() => {
            if (discardDialog?.isFormDirty(formKey) ?? false) {
              discardDialog?.show(formKey, () => {
                onCancel()
              })
              return
            }

            onCancel()
          }}
          disabled={isSaving}
        >
          Cancel
        </Grid.Button>

        <Grid.Button
          purple
          type='submit'
          style={{
            backgroundColor: '#EAE4FC',
            color: isSaving ? '#999' : '#6D45F1',
            padding: '.8rem 3rem'
          }}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : isNew ? 'Create' : 'Save'}
        </Grid.Button>
      </div>
    </form>
  )
}

interface ExpandingComponentProps {
  user?: User
  isSaving: boolean
  onSave: (user: User) => void
  onCancel: () => void
}

function ExpandingComponent ({ user, isSaving, onSave, onCancel }: ExpandingComponentProps): JSX.Element {
  return (
    <div>
      <Form
        // @ts-expect-error // It's fine
        initialValues={user}
        isSaving={isSaving}
        // @ts-expect-error // It's fine
        onSubmit={onSave}
        onCancel={onCancel}
      />
    </div>
  )
}
